import { NgClass } from '@angular/common';
import { ChangeDetectorRef, Component, computed, effect, inject, input, output, signal, untracked } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { DataType } from '@api/devices';
import { type ActionPointBase, type ModelActionPoint, type NormalizedDeviceData, type ValueValidationRule } from '@devices';
import { ConfirmationDialogComponent, SearchInputComponent } from '@ui';
import { debounceTime } from 'rxjs';

enum ValueValidationRuleConfig {
  ENUM = 'enum',
  MIN_MAX = 'min-max'
}

@Component({
  imports: [ReactiveFormsModule, SearchInputComponent, MatCheckboxModule, MatFormFieldModule, MatSelectModule, MatIcon, MatInputModule, MatSlideToggleModule, NgClass],
  selector: 'avs-action-point-selector',
  styleUrl: './action-points-selector.component.scss',
  templateUrl: './action-points-selector.component.html',
})
export class ActionPointSelectorComponent {
  availablePoints = input.required<ActionPointBase[]>();
  selectedPoints = input.required<ModelActionPoint[]>();
  availableDCDevices = input<NormalizedDeviceData[]>([]);
  showDCSelect = input(false);
  toggleCssClass = input('mb-5');

  selectedPointsChange = output<ModelActionPoint[]>();

  protected filteredActionPoints = computed(() => {
    const search = this.actionPointSearch().toLowerCase();
    const selectedPoints = this.selectedPoints();
    const showSelectedOnly = this.showSelectedOnly();
    const actionPoints = this.availablePoints().filter(point => !showSelectedOnly || selectedPoints.some(({ id }) => point.id === id));

    if (!search) {
      return actionPoints;
    }

    return actionPoints.filter(actionPoint => actionPoint.name.toLowerCase().includes(search));
  });

  protected actionPointSearch = signal('');
  protected showSelectedOnly = signal(false);
  protected actionPointEditId = signal<string | null>(null);
  protected readonly eActionPointValueDataType = DataType;
  protected readonly eValueValidationRuleConfig = ValueValidationRuleConfig;
  protected formGroups: { [id: string]: FormGroup } = {};
  protected configTitle: { [index: string]: string } = {
    [ValueValidationRuleConfig.ENUM]: 'Enumerated Values',
    [ValueValidationRuleConfig.MIN_MAX]: 'Min/Max',
  };

  private fb = inject(FormBuilder);
  private initialFormValues: { [id: string]: any } = {};
  private cdr = inject(ChangeDetectorRef);
  private dialog = inject(MatDialog);

  constructor() {
    effect(() => {
      this.formGroups = {};
      this.initialFormValues = {};
      this.actionPointEditId.set(null);

      const availablePoints = this.availablePoints();
      const selectedPoints = untracked(() => this.selectedPoints());
      const availableDCDevices = this.availableDCDevices();

      availablePoints.forEach(point => {
        const existing = selectedPoints.find(p => p.id === point.id);
        const existingDc = availableDCDevices.find(p => p.id === existing?.dcUid);

        const fg = this.fb.group({
          checked: [!!existing],
          dcUid: [existingDc?.id || null],
          id: [existing?.id || point.id],
          name: [existing?.name || point.name],
          valueDataType: [existing?.valueDataType || '', Validators.required],
          valueValidationRule: this.fb.group({}),
        });

        if (existing?.valueDataType) {
          this.setValueValidationRuleControls(fg, existing.valueDataType, existing.valueValidationRule);
        }

        fg.get('valueDataType')?.valueChanges.subscribe((valueDataType: string | null) => {
          this.setValueValidationRuleControls(fg, valueDataType as DataType);
        });

        fg.get('checked')?.valueChanges.pipe(
          debounceTime(10),
        ).subscribe((checked) => {
          if (checked && !this.formGroups[point.id].value.valueDataType) {
            this.formGroups[point.id].patchValue({
              valueDataType: DataType.TEXT,
            });
          }
          this.emitSelected();
        });

        this.formGroups[point.id] = fg;
      });
    });
  }

  protected startEdit(actionPointId: string) {
    this.actionPointEditId.set(actionPointId);
    this.initialFormValues[actionPointId] = this.formGroups[actionPointId].value;
  }

  protected cancelEdit(actionPointId: string) {
    this.formGroups[actionPointId].patchValue(this.initialFormValues[actionPointId]);
    this.formGroups[actionPointId].markAsUntouched();
    this.actionPointEditId.set(null);
  }

  protected patchPoint(actionPointId: string) {
    this.actionPointEditId.set(null);
    if (this.formGroups[actionPointId].value.checked) {
      this.emitSelected();
    }
  }

  protected clear(actionPoint: ActionPointBase) {
    ConfirmationDialogComponent.open(this.dialog, {
      description: `Are you sure you want to clear configurations for [${actionPoint.friendlyName || actionPoint.name}] action point?`,
      title: 'Clear',
    })
      .subscribe(confirmation => {
        if (confirmation) {
          this.formGroups[actionPoint.id].reset({
            checked: this.formGroups[actionPoint.id].value.checked,
            id: this.formGroups[actionPoint.id].value.id,
            name: this.formGroups[actionPoint.id].value.name,
            valueDataType: DataType.TEXT,
            valueValidationRule: null,
          });

          if (this.formGroups[actionPoint.id].value.checked) {
            this.emitSelected();
          }
        }
      });
  }

  private setValueValidationRuleControls(
    fg: FormGroup,
    valueDataType: DataType | null,
    existingRule?: ValueValidationRule | null,
  ) {
    this.clearFormGroup(fg.get('valueValidationRule') as FormGroup);

    if (!valueDataType) {
      return;
    }

    const valueRule = fg.get('valueValidationRule') as FormGroup;

    if (valueDataType === this.eActionPointValueDataType.TEXT || valueDataType === this.eActionPointValueDataType.BOOLEAN) {
      valueRule.addControl('values', this.fb.control(existingRule?.values || ''));
    }

    if (valueDataType === this.eActionPointValueDataType.FLOAT || valueDataType === this.eActionPointValueDataType.INTEGER) {
      let initialConfig: ValueValidationRuleConfig | '';

      initialConfig = this.eValueValidationRuleConfig.ENUM;
      if (existingRule?.min || existingRule?.max) {
        initialConfig = this.eValueValidationRuleConfig.MIN_MAX;
      }

      this.cdr.detectChanges();
      valueRule.addControl('config', this.fb.control(initialConfig, Validators.required));

      if (initialConfig === this.eValueValidationRuleConfig.ENUM) {
        valueRule.addControl('values', this.fb.control(existingRule?.values || ''));
      } else if (initialConfig === this.eValueValidationRuleConfig.MIN_MAX) {
        valueRule.addControl('min', this.fb.control(existingRule?.min, Validators.required));
        valueRule.addControl('max', this.fb.control(existingRule?.max, Validators.required));
        valueRule.addControl('step', this.fb.control(existingRule?.step || 1));
      }

      fg.get('valueValidationRule')?.get('config')?.valueChanges.subscribe((cfg: ValueValidationRuleConfig | null) => {
        const valueRule = fg.get('valueValidationRule') as FormGroup;

        valueRule.removeControl('values');
        valueRule.removeControl('min');
        valueRule.removeControl('max');
        valueRule.removeControl('step');

        if (cfg === this.eValueValidationRuleConfig.ENUM) {
          valueRule.addControl('values', this.fb.control(''));
        }

        if (cfg === this.eValueValidationRuleConfig.MIN_MAX) {
          valueRule.addControl('min', this.fb.control(null, Validators.required));
          valueRule.addControl('max', this.fb.control(null, Validators.required));
          valueRule.addControl('step', this.fb.control(1));
        }
      });
    }
  }

  private clearFormGroup(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach(key => {
      formGroup.removeControl(key);
    });
  }

  private emitSelected() {
    const selected = Object.values(this.formGroups)
      .map(fg => fg.value)
      .filter(v => v.checked);

    this.selectedPointsChange.emit(selected);
  }
}
